import { useField } from '@hooks/useField';
import { IForm, useForm } from '@hooks/useForm';
import { IModalWindow } from '@hooks/useModalWindow';
import { IJustificativaRequest } from '@hooks/useRequest';
import { useValidation } from '@hooks/useValidation';
import { DialogActions, Grid } from '@material-ui/core';
import { yup } from '@validations/Yup';
import React from 'react';
import { ModalWindow } from './ModalWindow';
import { ModalWindowContent } from './ModalWindowContent';
import { ModalWindowTitle } from './ModalWindowTitle';
import { SecondaryButton } from '../Buttons/SecondaryButton';
import { StringField } from '../Fields/StringField';

export type JustificativaModalProps = {
    modal: IModalWindow,
    title: string,
    cancelText?: string,
    confirmText: string,
    disableBackdropClick?: boolean,
    disabled: boolean,
    onConfirm: (values: IJustificativaRequest, form: IForm<IJustificativaRequest>) => void,
}

function JustificativaModalComponent ({ 
    modal, 
    title, 
    confirmText, 
    disableBackdropClick, 
    disabled, 
    onConfirm,
}: JustificativaModalProps) {
    const justificativaInputRef = React.createRef<HTMLInputElement>();

    const form = useForm<IJustificativaRequest>({
        justificativa: useField()
    });

    const { fields } = form;

    const validator = useValidation<IJustificativaRequest>({
        justificativa: yup.string().trim().required()
    })

    const handleConfirm = React.useCallback(() => {
        const values = form.getInputValues();

        form.clearErrors();
        validator.validate(values)
            .then(() => {
                onConfirm(values, form);
            })
            .catch(form.setErrorsFromYup);
    }, [form, onConfirm, validator]);

    const handleEnter = React.useCallback(() => {
        form.reset();
        justificativaInputRef.current?.focus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [justificativaInputRef]);    

    return (
        <ModalWindow
            modal={modal}
            size="sm"
            disableBackdropClick={disableBackdropClick}
            disabled={disabled}
            TransitionProps={{
                onEnter: handleEnter
            }}
        >
            <ModalWindowTitle
                title={title}
            />

            <ModalWindowContent>
                <Grid item xs={12}>
                    <StringField 
                        inputRef={justificativaInputRef}
                        field={fields.justificativa}
                        label="Justificativa"
                        required
                        multiline
                    />
                </Grid>
            </ModalWindowContent>

            <DialogActions>
                <SecondaryButton onClick={handleConfirm} disabled={disabled}>{ confirmText }</SecondaryButton>
            </DialogActions>
        </ModalWindow>
    )
}

export const JustificativaModal = React.memo(JustificativaModalComponent);